<template>
  <w-flex fluid direction="col" align="stretch" class="w-address">
    <w-flex v-if="calculatedTitle || $slots.title" class="w-address__title">
      <slot name="title"> {{ calculatedTitle }} </slot>
    </w-flex>

    <w-row :gap="4">
      <w-col :size="12">
        <w-select
          :model="model.Wojewodztwo"
          :options="wojOptions"
          :variant="variant"
          @change="onWojChange"
        />
      </w-col>

      <w-col :size="12">
        <w-select
          :model="model.Powiat"
          :options="powOptions"
          :variant="variant"
          @change="onPowChange"
        />
      </w-col>

      <w-col :size="12">
        <w-select
          :model="model.Gmina"
          :options="gmiOptions"
          :variant="variant"
          @change="onGmiChange"
        />
      </w-col>

      <w-col class="w-address__postal-code">
        <w-input :model="model.KodPocztowy" :variant="variant" />
      </w-col>

      <w-col class="w-address__city">
        <w-input :model="model.Miejscowosc" :variant="variant" />
      </w-col>

      <w-col :size="12">
        <w-input :model="model.Poczta" :variant="variant" />
      </w-col>

      <w-col :size="12">
        <w-row :gap="4">
          <w-col class="w-address__street">
            <w-input :model="model.Ulica" :variant="variant" />
          </w-col>

          <w-col class="w-address__numbers">
            <w-row :gap="4">
              <w-col>
                <w-input :model="model.Nr" :variant="variant" />
              </w-col>

              <w-col>
                <w-input :model="model.NumerLokalu" :variant="variant" />
              </w-col>
            </w-row>
          </w-col>
        </w-row>
      </w-col>
    </w-row>
  </w-flex>
</template>

<script setup lang="ts">
import { withInputVariantProps, type SelectOption } from '@witkac/vue3-components';
import { computed, ref, type PropType } from 'vue';

import { getFromVue3Compontents } from '@/utils/resolveVue3Service';

import type { AddressSchema } from '../../shared/addressSchema';

const props = defineProps({
  /**
   * Adres do wyświetlenia
   */
  model: {
    type: Object as PropType<AddressSchema>,
    required: true,
  },

  /**
   * Tytuł
   */
  title: {
    type: String,
  },

  /**
   * Ograniczone województwa
   */
  woj: {
    type: Array as PropType<string[]>,
  },

  ...getFromVue3Compontents('withInputVariantProps', withInputVariantProps),
});
const emit = defineEmits<{
  (e: 'woj-changed', model: AddressSchema, pow: string[], gmi: string[]): void;
  (e: 'pow-changed', model: AddressSchema, pow: string[], gmi: string[]): void;
  (e: 'gmi-changed', model: AddressSchema, pow: string[], gmi: string[]): void;
}>();

const pow = ref<string[]>([]);
const gmi = ref<string[]>([]);

const onWojChange = () => {
  emit('woj-changed', props.model, pow.value, gmi.value);
};

const onPowChange = () => {
  emit('pow-changed', props.model, pow.value, gmi.value);
};

const onGmiChange = () => {
  emit('gmi-changed', props.model, pow.value, gmi.value);
};

const wojOptions = computed<SelectOption[]>(
  () =>
    props.woj?.map((w) => ({
      value: w,
      text: w,
    })) || [],
);

const powOptions = computed<SelectOption[]>(() =>
  pow.value.map((p) => ({
    value: p,
    text: p,
  })),
);

const gmiOptions = computed<SelectOption[]>(() =>
  gmi.value.map((g) => ({
    value: g,
    text: g,
  })),
);

onWojChange();
onPowChange();

const calculatedTitle = computed(() => {
  if (props.title) {
    return props.title;
  }

  if (props.model.$title) {
    return props.model.$title;
  }
  return props.title;
});
</script>

<style lang="scss">
.w-address {
  padding: 12px;
  border: solid 1px w.color('neutral', 100);
  border-radius: w.radius('regular');

  &__title {
    width: fit-content;
    background: w.color('neutral', 50);
    font-size: w.font('xsmall');
    font-weight: w.weight('semi-bold');
    color: w.color('neutral', 800);
    border-radius: w.radius('regular');
    padding: 2px 8px;
    text-transform: uppercase;
    margin-bottom: 24px;
  }

  &__street,
  &__city {
    min-width: 220px;
  }

  &__postal-code {
    flex: 0;
    flex-basis: 160px;
  }

  &__numbers {
    flex: 0;
    flex-basis: 360px;
  }
}
</style>
